import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Select } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { marginBreakdown, type MarginBreakdown } from "../api/margin";
import {
  fxRate,
  ReportingClient,
  type Currency,
  type GlobalFX,
  type Period,
} from "../api/reporting";
import { NexpimView } from "../views";
import { ClientDrillDown } from "./sales/ClientDrillDown";
import { periodColumns } from "./sales/periods";

/* ------------------------------------------------------------- periods --- */

type PeriodMode = "MONTH" | "TRIMESTER" | "YEAR" | "ROLLING";
type Rolling =
  | "YTD"
  | "FOUR_WEEKS"
  | "TWELVE_WEEKS"
  | "FIFTY_TWO_WEEKS"
  | "LAST_MONTH"
  | "LAST_3_MONTHS"
  | "LAST_12_MONTHS";

function iso(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function endOfMonth(year: number, month: number): string {
  return iso(new Date(year, month + 1, 0));
}

/** Resolves the four picker modes to dates — the `LocalDateUtil` mapping. */
function resolvePeriod(
  mode: PeriodMode,
  year: number,
  month: number,
  trimester: number,
  rolling: Rolling,
): Period {
  const now = new Date();
  switch (mode) {
    case "MONTH":
      return { from: iso(new Date(year, month, 1)), to: endOfMonth(year, month) };
    case "TRIMESTER": {
      const start = (trimester - 1) * 3;
      return { from: iso(new Date(year, start, 1)), to: endOfMonth(year, start + 2) };
    }
    case "YEAR":
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    case "ROLLING": {
      const weeksBack = (weeks: number) => {
        const from = new Date(now);
        from.setDate(from.getDate() - weeks * 7);
        return { from: iso(from), to: iso(now) };
      };
      const monthsWindow = (back: number) => ({
        from: iso(new Date(now.getFullYear(), now.getMonth() - back, 1)),
        to: endOfMonth(now.getFullYear(), now.getMonth() - 1),
      });
      switch (rolling) {
        case "YTD":
          return { from: `${now.getFullYear()}-01-01`, to: iso(now) };
        case "FOUR_WEEKS":
          return weeksBack(4);
        case "TWELVE_WEEKS":
          return weeksBack(12);
        case "FIFTY_TWO_WEEKS":
          return weeksBack(52);
        case "LAST_MONTH":
          return monthsWindow(1);
        case "LAST_3_MONTHS":
          return monthsWindow(3);
        case "LAST_12_MONTHS":
          return monthsWindow(12);
      }
    }
  }
}

/* ----------------------------------------------------------------- page --- */

interface Row extends MarginBreakdown {
  id: number;
  name: string;
  currency: Currency | "";
}

/**
 * Sales → Client margin: the full cost waterfall per client — sales down
 * through discounts, programs, shipping, advertising, commissions, royalties
 * and COGS to net margin — over a period picked as a month, trimester, year or
 * rolling window. The arithmetic lives in `margin.ts`, ported from the Java
 * getters that never reach the wire.
 */
export function ClientMarginPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const client = useMemo(() => new ReportingClient(http), [http]);
  const allowed = hasView(NexpimView.SALES_CLIENT_MARGIN);

  const now = new Date();
  const [mode, setMode] = useState<PeriodMode>("ROLLING");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [trimester, setTrimester] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [rolling, setRolling] = useState<Rolling>("YTD");

  const period = useMemo(
    () => resolvePeriod(mode, year, month, trimester, rolling),
    [mode, year, month, trimester, rolling],
  );

  const [drill, setDrill] = useState<Row | null>(null);

  /**
   * The drill-down runs on the standard month, quarter and year, not on this
   * screen's picker. The picker names one arbitrary window, which has no
   * previous period to compare against, so mapping it onto the dialog's three
   * comparisons would mean inventing two of them.
   */
  const drillColumns = useMemo(() => periodColumns("previous"), []);

  const { data, isLoading, error } = useQuery({
    queryKey: ["client-margin", period.from, period.to],
    queryFn: async () => {
      const [fx, aggregates] = await Promise.all([
        client.globalFx(),
        client.aggregateByClient(period),
      ]);

      const rows: Row[] = aggregates
        .filter((aggregate) => aggregate.client?.cli_id !== undefined)
        .map((aggregate): Row => {
          const currency = (aggregate.client?.cli_currency ?? aggregate.currency ?? "") as
            | Currency
            | "";
          const rate = fxRate(fx, currency || null);
          return {
            id: aggregate.client!.cli_id,
            name: aggregate.client?.cli_name1 ?? `#${aggregate.client!.cli_id}`,
            currency,
            ...marginBreakdown(aggregate, rate),
          };
        })
        .sort((a, b) => b.netMargin - a.netMargin);

      // Global net-margin % per currency for the header badges, as in Vaadin.
      const globalPercent = (currency: Currency) => {
        const subset = rows.filter((row) => row.currency === currency);
        const sales = subset.reduce((sum, row) => sum + row.sales, 0);
        const margin = subset.reduce((sum, row) => sum + row.netMargin, 0);
        return sales === 0 ? 0 : (margin / sales) * 100;
      };

      // Denominators for the two share columns: total units regardless of
      // currency, and total profit converted to CAD — the same `global_units_sold`
      // and `global_pnl` the Vaadin grid computed before painting.
      const globalUnits = rows.reduce((sum, row) => sum + row.units, 0);
      const globalPnl = rows.reduce(
        (sum, row) => sum + row.netMargin * fxRate(fx, row.currency || null),
        0,
      );

      return {
        rows,
        fx: fx as GlobalFX,
        globalCad: globalPercent("CAD"),
        globalUsd: globalPercent("USD"),
        globalUnits,
        globalPnl,
      };
    },
    enabled: allowed,
    staleTime: 5 * 60_000,
  });

  const globalUnits = data?.globalUnits ?? 0;
  const globalPnl = data?.globalPnl ?? 0;
  const fx = data?.fx;

  const cols = useMemo(() => {
    const c = defineColumns<Row>();
    const money = (value: number) => Math.round(value);
    const percent = (value: number) => `${(value * 100).toFixed(2)}%`;
    return [
      c.text("name", (row) => row.name, { headerKey: "column.client", priority: 1 }),
      c.text("currency", (row) => row.currency, {
        headerKey: "column.currency",
        filter: "select",
        priority: 2,
      }),
      c.number("units", (row) => row.units, { headerKey: "margin.units", priority: 4 }),
      c.number("sales", (row) => money(row.sales), { headerKey: "margin.sales", priority: 3 }),
      c.number("discounts", (row) => money(row.discounts), { headerKey: "margin.discounts" }),
      c.number("returns", (row) => money(row.returns), { headerKey: "margin.returns", defaultVisible: false }),
      c.number("programs", (row) => money(row.programs), { headerKey: "margin.programs", defaultVisible: false }),
      c.number("total_programs", (row) => money(row.totalPrograms), { headerKey: "margin.total-programs" }),
      c.number("shipping", (row) => money(row.shippingCost), { headerKey: "margin.shipping", defaultVisible: false }),
      c.number("advertising", (row) => money(row.advertisingCost), { headerKey: "margin.advertising", defaultVisible: false }),
      c.number("advertising_sales", (row) => money(row.advertisingSales), {
        headerKey: "margin.advertising-sales",
        defaultVisible: false,
      }),
      c.number("commissions", (row) => money(row.commissions), { headerKey: "margin.commissions", defaultVisible: false }),
      c.number("royalties", (row) => money(row.royalties), { headerKey: "margin.royalties", defaultVisible: false }),
      c.number("cogs", (row) => money(row.cogs), { headerKey: "column.cogs" }),
      c.number("total_cost", (row) => money(row.totalCost), { headerKey: "margin.total-cost" }),
      c.number("gross_margin", (row) => money(row.grossMargin), { headerKey: "margin.gross" }),
      c.number("net_margin", (row) => money(row.netMargin), { headerKey: "margin.net", priority: 3 }),
      c.custom(
        "net_percent",
        (row) => Math.round(row.netMarginPercent * 1000) / 10,
        (row) => {
          const percent = row.netMarginPercent * 100;
          return (
            <Badge variant={percent >= 20 ? "success" : percent >= 0 ? "warning" : "error"} size="sm">
              {percent.toFixed(1)}%
            </Badge>
          );
        },
        { headerKey: "margin.net-percent", priority: 3 },
      ),
      // Share of the business by units, and share of total profit. Both are
      // ratios against the whole period, so they only mean anything with the
      // full client list loaded — which is why they are computed here rather
      // than from the filtered rows.
      c.custom(
        "percent_sales",
        (row) => (globalUnits === 0 ? 0 : row.units / globalUnits),
        (row) => (
          <span className="tabular-nums">
            {percent(globalUnits === 0 ? 0 : row.units / globalUnits)}
          </span>
        ),
        { headerKey: "margin.percent-sales", filter: "range", align: "right" },
      ),
      c.custom(
        "percent_net_margin",
        (row) =>
          globalPnl === 0 ? 0 : (row.netMargin * fxRate(fx, row.currency || null)) / globalPnl,
        (row) => (
          <span className="tabular-nums">
            {percent(
              globalPnl === 0 ? 0 : (row.netMargin * fxRate(fx, row.currency || null)) / globalPnl,
            )}
          </span>
        ),
        { headerKey: "margin.percent-net-margin", filter: "range", align: "right" },
      ),
    ];
  }, [fx, globalPnl, globalUnits]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.sales-client-margin", "Client margin")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const years = Array.from({ length: 6 }, (_, index) => now.getFullYear() - index);
  const months = Array.from({ length: 12 }, (_, index) => ({
    value: String(index),
    label: new Date(2000, index, 1).toLocaleString(undefined, { month: "long" }),
  }));

  const periodControls = (
    // Period controls sit in the grid toolbar rather than the top bar: they
    // belong beside the counts they change. Labels ride on the controls
    // themselves rather than above them, so the row stays one control high and
    // level with the search box — the options already name themselves.
    <div className="flex items-center gap-1.5">
      <Select
        aria-label={t("margin.period-mode", "Period")}
        value={mode}
        options={[
          { value: "MONTH", label: t("margin.mode.month", "Month") },
          { value: "TRIMESTER", label: t("margin.mode.trimester", "Trimester") },
          { value: "YEAR", label: t("margin.mode.year", "Year") },
          { value: "ROLLING", label: t("margin.mode.rolling", "Rolling") },
        ]}
        onChange={(event) => setMode(event.target.value as PeriodMode)}
        className="h-8 w-32"
      />
      {mode !== "ROLLING" && (
        <Select
          aria-label={t("analysis.year", "Year")}
          value={String(year)}
          options={years.map((value) => ({ value: String(value), label: String(value) }))}
          onChange={(event) => setYear(Number(event.target.value))}
          className="h-8 w-24"
        />
      )}
      {mode === "MONTH" && (
        <Select
          aria-label={t("margin.mode.month", "Month")}
          value={String(month)}
          options={months}
          onChange={(event) => setMonth(Number(event.target.value))}
          className="h-8 w-36"
        />
      )}
      {mode === "TRIMESTER" && (
        <Select
          aria-label={t("margin.mode.trimester", "Trimester")}
          value={String(trimester)}
          options={[1, 2, 3, 4].map((value) => ({ value: String(value), label: `T${value}` }))}
          onChange={(event) => setTrimester(Number(event.target.value))}
          className="h-8 w-20"
        />
      )}
      {mode === "ROLLING" && (
        <Select
          aria-label={t("margin.window", "Window")}
          value={rolling}
          options={(
            [
              "YTD",
              "FOUR_WEEKS",
              "TWELVE_WEEKS",
              "FIFTY_TWO_WEEKS",
              "LAST_MONTH",
              "LAST_3_MONTHS",
              "LAST_12_MONTHS",
            ] as Rolling[]
          ).map((value) => ({ value, label: value.replaceAll("_", " ") }))}
          onChange={(event) => setRolling(event.target.value as Rolling)}
          className="h-8 w-40"
        />
      )}
    </div>
  );

  return (
    <>
      <PageHeader
        title={t("navigation.item.sales-client-margin", "Client margin")}
        subtitle={t("margin.subtitle", "From billed to net margin, per client.")}
        badges={
          data ? (
            <>
              {/* The period moved off the subtitle, which names the page, and
                  into a badge, which is where a page's current scope reads. */}
              <Badge variant="neutral" size="sm">{`${period.from} → ${period.to}`}</Badge>
              <Badge variant="neutral" size="sm">
                CAD {data.globalCad.toFixed(1)}%
              </Badge>
              <Badge variant="neutral" size="sm">
                USD {data.globalUsd.toFixed(1)}%
              </Badge>
            </>
          ) : null
        }
      />

      {error ? (
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      ) : (
        <DataTable
          data={data?.rows ?? []}
          columns={cols}
          getRowId={(row) => String(row.id)}
          loading={isLoading}
          onRowClick={(row) => setDrill(row)}
          toolbarExtra={periodControls}
          emptyMessage={t("table.empty", "Nothing to show")}
        />
      )}

      {drill && (
        <ClientDrillDown client={drill} columns={drillColumns} onClose={() => setDrill(null)} />
      )}
    </>
  );
}
